import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import TransferOwnerDialog from '../components/TransferOwnerDialog';

type Profile = {
  id: number;
  name: string;
  email: string;
  role: string;
  about_me: string | null;
  created_at: string | null;
  approved_at: string | null;
  last_login_at: string | null;
};

type ArchiveRow = {
  id: number;
  domain: string;
  path_prefix: string | null;
  status: string;
  pages_fetched: number | null;
  errors_count: number | null;
  requires_login: boolean | number;
  last_crawled_at: string | null;
  created_at: string | null;
};

type SubmissionRow = {
  id: number;
  domain: string;
  seed_url: string;
  status: string;
  submission_note: string | null;
  rejection_reason: string | null;
  created_at: string | null;
  reviewed_at: string | null;
};

type Detail = {
  user: Profile;
  archives: ArchiveRow[];
  submissions: SubmissionRow[];
};

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<{ id: number; domain: string } | null>(null);
  const [archiveQuery, setArchiveQuery] = useState('');

  function reload() {
    if (!id) return;
    api<{ data: Detail }>(`/admin/users/${id}`)
      .then((r) => setDetail(r.data))
      .catch(() => setError('Could not load user.'));
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const filteredArchives = useMemo(() => {
    if (detail === null) return [];
    const q = archiveQuery.trim().toLowerCase();
    if (q === '') return detail.archives;
    return detail.archives.filter((a) => {
      if (a.domain.toLowerCase().includes(q)) return true;
      if (a.path_prefix && a.path_prefix.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [detail, archiveQuery]);

  if (error) return <div className="card"><p className="error">{error}</p></div>;
  if (detail === null) return <div className="card"><p className="muted">Loading…</p></div>;

  const { user, archives, submissions } = detail;

  return (
    <>
      <div className="card">
        <p style={{ marginTop: 0, marginBottom: 12 }}>
          <Link to="/admin/users">← All users</Link>
        </p>
        <h2 style={{ marginTop: 0 }}>
          {user.name}{' '}
          {user.role === 'admin' && (
            <span className="status-pill admin" style={{ background: 'var(--pill-bg-retry)', color: 'var(--pill-fg-retry)', fontSize: 12, marginLeft: 6 }}>admin</span>
          )}
          {user.approved_at === null && (
            <span style={{ color: '#a60', fontSize: 13, marginLeft: 6 }}>(pending approval)</span>
          )}
        </h2>
        <table>
          <tbody>
            <tr><td className="muted" style={{ width: 160 }}>Email</td><td>{user.email}</td></tr>
            <tr>
              <td className="muted">About</td>
              <td style={{ wordBreak: 'break-word' }}>
                {user.about_me ?? <span className="muted">—</span>}
              </td>
            </tr>
            <tr>
              <td className="muted">Joined</td>
              <td>{user.created_at ? new Date(user.created_at).toLocaleString() : '—'}</td>
            </tr>
            <tr>
              <td className="muted">Approved</td>
              <td>{user.approved_at ? new Date(user.approved_at).toLocaleString() : <span className="muted">—</span>}</td>
            </tr>
            <tr>
              <td className="muted">Last login</td>
              <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : <span className="muted">never</span>}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Archives ({archives.length})</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Crawls this user created — directly (admin) or via submission approval.
        </p>
        {archives.length === 0 ? (
          <p className="muted">None.</p>
        ) : (
          <>
            <input
              type="text"
              value={archiveQuery}
              onChange={(e) => setArchiveQuery(e.target.value)}
              placeholder="Filter by domain or path…"
              style={{ marginBottom: 8, padding: '4px 8px', width: 280 }}
            />
            {filteredArchives.length === 0 ? (
              <p className="muted">No archives match.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Pages</th>
                    <th style={{ textAlign: 'right' }}>Errors</th>
                    <th>Auth</th>
                    <th>Last crawl</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArchives.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <Link to={`/archives/${a.id}`}>{a.domain}</Link>
                        {a.path_prefix && (
                          <div className="muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>{a.path_prefix}</div>
                        )}
                      </td>
                      <td>{a.status}</td>
                      <td style={{ textAlign: 'right' }}>{a.pages_fetched ?? 0}</td>
                      <td style={{ textAlign: 'right' }}>
                        {a.errors_count && a.errors_count > 0
                          ? <strong>{a.errors_count}</strong>
                          : <span className="muted">0</span>}
                      </td>
                      <td>
                        {a.requires_login
                          ? <span className="muted">login</span>
                          : <span className="muted">—</span>}
                      </td>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                        {a.last_crawled_at ? new Date(a.last_crawled_at).toLocaleString() : '—'}
                      </td>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                        {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={() => setTransferTarget({ id: a.id, domain: a.domain })}
                        >
                          Transfer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
      {transferTarget && (
        <TransferOwnerDialog
          archiveId={transferTarget.id}
          domain={transferTarget.domain}
          currentOwnerId={user.id}
          onClose={() => setTransferTarget(null)}
          onTransferred={reload}
        />
      )}

      <div className="card">
        <h2>Submissions ({submissions.length})</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Sites awaiting review or rejected. Approved submissions become
          archives and appear in the table above instead.
        </p>
        {submissions.length === 0 ? (
          <p className="muted">None.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Domain</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Reviewed</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => (
                <tr key={s.id}>
                  <td style={{ wordBreak: 'break-word' }}>
                    <div>{s.domain}</div>
                    <div className="muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>{s.seed_url}</div>
                  </td>
                  <td>
                    {s.status === 'rejected'
                      ? <span style={{ color: 'var(--fg-error)' }}>rejected</span>
                      : <span className="muted">awaiting review</span>}
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {s.reviewed_at ? new Date(s.reviewed_at).toLocaleString() : '—'}
                  </td>
                  <td style={{ maxWidth: 360, wordBreak: 'break-word' }}>
                    {s.status === 'rejected' && s.rejection_reason ? (
                      <span className="error">{s.rejection_reason}</span>
                    ) : s.submission_note ? (
                      <span className="muted">{s.submission_note}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
